import * as React from 'react';
import SuperCritical from 'lib/supercritical';
import { tzmoment } from 'lib/tzmoment';
import { noDefault } from 'lib/eventWrappers';

import {
    MovementViewModel,
    OriginalPilotJobOrderViewModel,
    OriginalPilotOrder,
    OriginalPortCallViewModel,
    OriginalVesselViewModel
} from './originalpilotorder';

import { PCModal } from 'portcall/pcmodal/view';

export const PrintOriginalOrderLink = SuperCritical(({ model: m } : { model : OriginalPilotOrder }) =>
    m.LinkAvailable() ?
        <div className="pjo-actions-menu-item">
            {m.Loading() ?
                <a href="" onClick={noDefault(() => {})}>Printing...</a> :
                <a href="" onClick={noDefault(m.PrintOrder)}>Print Original Order</a>
            }
        </div> :
        null
);

export const OriginalPilotOrderView = SuperCritical(({ model: m } : { model : OriginalPilotOrder }) =>
    <div>
        <OrderUnavailablePopup model={m} />
        <OriginalPilotOrderPrintView model={m} />
    </div>
);

const OrderUnavailablePopup = SuperCritical(({ model: m } : { model : OriginalPilotOrder }) =>
    <PCModal shown={m.ShowOrderUnavailableMsg} className="original-pilot-order-unavailable" onBackDropClick={() => m?.ShowOrderUnavailableMsg(false)}>
        <div className="modal-content pc-card">
            <div>
                Sorry, the receipt you requested is not available for this pilot order.
            </div>
            <div className="controls">
                <button
                    className="pc-button"
                    onClick={() => m.ShowOrderUnavailableMsg(false)}
                >
                    OK
                </button>
            </div>
        </div>
    </PCModal>
);

const OriginalPilotOrderPrintView = SuperCritical(({ model: m } : { model : OriginalPilotOrder }) => {
    const originalOrder = m.DisplayedOrder();
    if (!originalOrder) return null;

    return (
        <div className="original-pilot-order">
            <PrintViewTitle model={m} />
            <OriginalOrderSubmission order={originalOrder} />
            <OriginalOrderVessel order={originalOrder} />
            <OriginalOrderMovements order={originalOrder} />
            <OriginalOrderAgentAndBilling order={originalOrder} />
        </div>
    );
});

const PrintViewTitle = SuperCritical(({ model: m } : { model : OriginalPilotOrder }) =>
    <h1 className="original-order-title">
        Original Pilot Orders &mdash;
        {' '}
        {m.PilotAgency() &&
            m.PilotAgency()!.Organization() &&
            m.PilotAgency()!.Organization()!.Name()
        }
    </h1>
);

const OriginalOrderSubmission = SuperCritical(({ order: o } : { order : OriginalPortCallViewModel }) => {
    const
        requestor = o.Model.Requestor(),
        requestingOrg = requestor && requestor.Organization(),
        reservations = o.Model.Reservations(),
        requestDate = reservations.length ? reservations[0].RequestDate() : null;

    return (
        <div className="original-order-submission">
            <div>
                Submitted by:
                {' '}
                {requestor && requestor.FullName()}
                {', '}
                {requestingOrg && requestingOrg.Name()}
            </div>
            <div>
                Date submitted:
                {' '}
                <span className="submitted-date">
                    {requestDate && tzmoment(requestDate).format('DD MMM YYYY')}
                </span>
            </div>
        </div>
    );
});

const OriginalOrderVessel = SuperCritical(({ order: o } : { order : OriginalPortCallViewModel }) => {
    const v = o.Vessel();

    return (
        <div className="original-order-section original-order-vessel">
            <h2 className="original-order-section-header">{o.Model.DisplayVesselIdentifier()}</h2>
            <div className="original-order-section-body">
                <div className="vessel-details">
                    <LengthOverall order={o} />
                    <Flag vessel={v} />
                    <GrossTonnage vessel={v} />
                    <IMO vessel={v} />
                    <VesselType vessel={v} />
                    <CallSign vessel={v} />
                    <Beam vessel={v} />
                </div>
                <PortsOfCall order={o} />
            </div>
        </div>
    );
});

const LengthOverall = SuperCritical(({ order: o } : { order : OriginalPortCallViewModel }) =>
    <div className="vessel-section-item">
        <span className="vessel-section-item-label">Length Overall:</span>
        <span className="vessel-section-item-value">{o.LengthDisplay()}</span>
    </div>
);

const Flag = SuperCritical(({ vessel: v } : { vessel : OriginalVesselViewModel | null }) =>
    <div className="vessel-section-item">
        <span className="vessel-section-item-label">Flag:</span>
        <span className="vessel-section-item-value">{v && v.Model.Flag() && v.Model.Flag()!.Name()}</span>
    </div>
);

const GrossTonnage = SuperCritical(({ vessel: v } : { vessel : OriginalVesselViewModel | null }) =>
    <div className="vessel-section-item">
        <span className="vessel-section-item-label">Gross Tonnage:</span>
        <span className="vessel-section-item-value">{v && v.GrossTonnageDisplay()}</span>
    </div>
);

const IMO = SuperCritical(({ vessel: v } : { vessel : OriginalVesselViewModel | null }) =>
    <div className="vessel-section-item">
        <span className="vessel-section-item-label">IMO:</span>
        <span className="vessel-section-item-value">{v && v.Model.IMO()}</span>
    </div>
);

const VesselType = SuperCritical(({ vessel: v } : { vessel : OriginalVesselViewModel | null }) =>
    <div className="vessel-section-item">
        <span className="vessel-section-item-label">Vessel Type:</span>
        <span className="vessel-section-item-value">{v && v.Model.VesselType()}</span>
    </div>
);

const CallSign = SuperCritical(({ vessel: v } : { vessel : OriginalVesselViewModel | null }) =>
    <div className="vessel-section-item">
        <span className="vessel-section-item-label">Call Sign:</span>
        <span className="vessel-section-item-value">{v && v.Model.CallSign()}</span>
    </div>
);

const Beam = SuperCritical(({ vessel: v } : { vessel : OriginalVesselViewModel | null }) =>
    <div className="vessel-section-item">
        <span className="vessel-section-item-label">Beam:</span>
        <span className="vessel-section-item-value">{v && v.BeamDisplay()}</span>
    </div>
);

const PortsOfCall = SuperCritical(({ order: o } : { order : OriginalPortCallViewModel }) =>
    <div className="ports-of-call">
        <div className="vessel-section-item">
            <span className="vessel-section-item-label">Last Port:</span>
            <span className="vessel-section-item-value">{o.Model.LastPortOfCall()}</span>
        </div>
        <div className="vessel-section-item">
            <span className="vessel-section-item-label">Next Port:</span>
            <span className="vessel-section-item-value">{o.Model.NextPortOfCall()}</span>
        </div>
    </div>
);

const OriginalOrderMovements = SuperCritical(({ order: o } : { order : OriginalPortCallViewModel }) =>
    <div className="original-order-section original-order-movements">
        <h2 className="original-order-section-header">Movements</h2>
        <div className="original-order-section-body">
            {o.Movements().map(mvmt =>
                <MovementView movement={mvmt} key={mvmt.Key()} />
            )}
        </div>
    </div>
);

const MovementView = SuperCritical(({ movement: mvmt } : { movement : MovementViewModel }) =>
    <div className="original-order-mvmt">
        <MovementTimeAndStatus movement={mvmt} />
        <MovementDetails movement={mvmt} />
    </div>
);

const MovementTimeAndStatus = SuperCritical(({ movement: mvmt } : { movement : MovementViewModel }) =>
    <div className="mvmt-time-status">
        <div className="mvmt-date">{tzmoment(mvmt.MovementTime()).format('MMM D, YYYY')}</div>
        <div className="mvmt-type-time">
                {mvmt.Type() === 'in' ? 'Inbound:' : mvmt.Type() === 'out' ? 'Outbound:' : 'Shifting:'}
                {' '}
                {tzmoment(mvmt.MovementTime()).format('HH:mm')}
        </div>
        <div className="mvmt-unordered-indicator">
            {!mvmt.PilotJobOrder() && '(unordered)'}
        </div>
    </div>
);

const MovementDetails = SuperCritical(({ movement: mvmt } : { movement : MovementViewModel }) =>
    <div className="mvmt-details">
        <MovementLocation movement={mvmt} />
        <MovementDrillDown movement={mvmt} />
    </div>
);

const MovementLocation = SuperCritical(({ movement: mvmt } : { movement : MovementViewModel }) =>
    <div className="mvmt-location">
        <div className="mvmt-location-item">
            <span className="mvmt-location-item-label">From</span>
            <span className="mvmt-location-item-value">{mvmt.FromPoint.Location() && mvmt.FromPoint.Location()!.Name()}</span>
        </div>
        <div className="mvmt-location-item">
            <span className="mvmt-location-item-label">To</span>
            <span className="mvmt-location-item-value">{mvmt.ToPoint.Location() && mvmt.ToPoint.Location()!.Name()}</span>
        </div>
    </div>
);

const MovementDrillDown = SuperCritical(({ movement: mvmt } : { movement : MovementViewModel }) => {
    const
        pjo = mvmt.PilotJobOrder(),
        res = mvmt.Reservation();
    return (
        <div className="mvmt-drill-down">
            <div className="mvmt-drill-down-item">
                <span className="mvmt-drill-down-item-label">Deep Draft:</span>
                <span className="mvmt-drill-down-item-value">{pjo && pjo.DeepDraftDisplay()}</span>
            </div>
            <div className="mvmt-drill-down-item">
                <span className="mvmt-drill-down-item-label">Air Draft:</span>
                <span className="mvmt-drill-down-item-value">{pjo && pjo.AirDraftDisplay()}</span>
            </div>
            <div className="mvmt-drill-down-item">
                <span className="mvmt-drill-down-item-label">Side to:</span>
                <span className="mvmt-drill-down-item-value">{res && res.SideToText()}</span>
            </div>
            <div className="mvmt-drill-down-item">
                <span className="mvmt-drill-down-item-label">Footmarks:</span>
                <span className="mvmt-drill-down-item-value">{res && `${res.FootmarkNum()}-${res.EndmarkNum()}`}</span>
            </div>
        </div>
    );
});

const OriginalOrderAgentAndBilling = SuperCritical(({ order: o } : { order : OriginalPortCallViewModel }) =>
    <div className="original-order-section original-order-agent-billing">
        <h2 className="original-order-section-header">
            <span className="agent-header">Agent</span>
            <span className="billing-header">Billing Party/Address</span>
        </h2>
        <div className="original-order-section-body">
            <AgentView order={o} />
            <BillingEtcView order={o} />
            <RemarksView order={o} />
        </div>
    </div>
);

const AgentView = SuperCritical(({ order: o } : { order : OriginalPortCallViewModel }) => {
    const agent = o.Model.Agent();
    return (
        <div className="agent">
            {agent &&
                <div>
                    <div className="agent-name">{agent.FullName()}</div>
                    <div className="agent-contact-info">
                        <div>{agent.Organization() && agent.Organization()!.Name()}</div>
                        <div className="agent-email">{agent.User() && agent.User()!.Email()}</div>
                        <div>{agent.MainPhoneNumber() && agent.MainPhoneNumber()!.Number()}</div>
                    </div>
                </div>
            }
        </div>
    );
});

const BillingEtcView = SuperCritical(({ order: o } : { order : OriginalPortCallViewModel }) => {
    const pjo = o.TypicalPilotJobOrder();

    return (
        <div className="billing-etc">
            {pjo &&
                <div>
                    <BillToAddress pjo={pjo} />
                    <ServicesView pjo={pjo} />
                </div>
            }
        </div>
    );
});

const BillToAddress = SuperCritical(({ pjo } : { pjo : OriginalPilotJobOrderViewModel }) => {
    const billToAddr = pjo.Model.BillToAddress();
    return billToAddr ?
        <div className="bill-to-address">
            {pjo.Model.BillToAddress() || ''}
        </div> :
        null
});

const ServicesView = SuperCritical(({ pjo } : { pjo : OriginalPilotJobOrderViewModel }) =>
    <div className="services">
        <div className="services-item">
            <span className="services-item-label">Tug:</span>
            <span className="services-item-value">{pjo.Model.TugCompany() && pjo.Model.TugCompany()!.Name()}</span>
        </div>
        <div className="services-item">
            <span className="services-item-label">Tug #:</span>
            <span className="services-item-value">{pjo.Model.NumberOfTugs()}</span>
        </div>
        <div className="services-item">
            <span className="services-item-label">Lines:</span>
            <span className="services-item-value">{pjo.Model.LineHandlingCompany() && pjo.Model.LineHandlingCompany()!.Name()}</span>
        </div>
        <div className="services-item">
            <span className="services-item-label">Labor:</span>
            <span className="services-item-value">{pjo.Model.LaborStartTime() && tzmoment(pjo.Model.LaborStartTime()!).format("MMM D, YYYY [at] HH:mm")}</span>
        </div>
    </div>
);

const RemarksView = SuperCritical(({ order: o } : { order : OriginalPortCallViewModel }) =>
    <div className="remarks">
        <span className="remarks-label">Remarks:</span>
        <span className="remarks-value">
            {o.TypicalPilotJobOrder() && o.TypicalPilotJobOrder()!.Model.Remarks()}
        </span>
    </div>
);



// WEBPACK FOOTER //
// ./apps/pilots/pilotjoborderdetails/originalpilotorderview.tsx